/* eslint-disable security/detect-object-injection */
import { html, LitElement } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import { tabsStyles } from './tabs.styles';
import { LudsTab } from './tab';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import resetStyle from '../../shared/components/html-content/reset.style';
import { ludsBaseFont } from '../../shared/styles/typography';

/**
 * Tabs container component that manages tab state and selection.
 */
@customElement('luds-tabs')
export class LudsTabs extends LitElement {
  static override styles = [overrideDesignTokens, resetStyle, ludsBaseFont, tabsStyles];

  /**
   * The index of the active tab
   */
  @property({ type: Number, attribute: 'active-tab-index', reflect: true })
  activeTabIndex = 0;

  /**
   * Whether the tabs should be centered
   */
  @property({ type: Boolean, reflect: true })
  centered = false;

  @property({ type: Boolean })
  _isInternalUpdate = false;

  /**
   * The collection of tab elements
   */
  @queryAssignedElements({ selector: 'luds-tab' })
  private _tabElements!: LudsTab[];

  override firstUpdated(): void {
    this._initializeTabs();
    this.requestUpdate();
  }

  override render() {
    return html`
      <ul class="tabs ${this.centered ? 'tabs--centered' : ''}" role="tablist">
        ${this._tabElements?.map(
          (tab, index) => html`
            <li role="presentation" class="tabs__item" @click=${() => this._onTabClick(index)}>
              <button
                class="tabs__trigger tabs__trigger--default ${tab.active ? 'tabs__trigger--active' : ''}"
                role="tab"
                ?disabled=${tab.disabled}
                aria-disabled=${tab.disabled ? 'true' : 'false'}
                aria-selected=${tab.active ? 'true' : 'false'}>
                ${tab.title} ${tab.counter ? html`<span class="tabs__counter">${tab.counter}</span>` : ''}
              </button>
            </li>
          `
        )}
      </ul>

      <div class="tab-content">
        <slot></slot>
      </div>
    `;
  }

  /**
   * Initialize the tabs by setting up initial state and event listeners
   */
  private _initializeTabs() {
    if (!this._tabElements.length) return;
    this._updateActiveTab();
  }

  /**
   * Update which tab is active based on activeTabIndex
   */
  private _updateActiveTab() {
    if (!this._tabElements.length) return;

    const activeIndex = Math.max(0, Math.min(this.activeTabIndex, this._tabElements.length - 1));

    // Update tab states
    this._tabElements.forEach((tab, index) => {
      tab.active = index === activeIndex;
    });
  }

  /**
   * Handle tab click events
   */
  private _onTabClick(index: number): void {
    // Validate index is within bounds
    if (index < 0 || index >= this._tabElements.length) return;

    // Check if tab is disabled using a safe accessor
    const tab = this._tabElements[index];
    if (tab?.disabled) return;

    this.activeTabIndex = index;
    this._dispatchTabSelectEvent(index);
  }

  /**
   * Dispatch a custom event when a tab is selected
   */
  private _dispatchTabSelectEvent(index: number): void {
    // Validate index is within bounds
    if (index < 0 || index >= this._tabElements.length) return;

    const selectedTab = this._tabElements[index];
    this.dispatchEvent(
      new CustomEvent('tab-select', {
        detail: {
          index,
          tab: selectedTab,
        },
        bubbles: true,
        composed: true,
      })
    );
    this._updateActiveTab();
  }

  setActiveTabIndex(index: number): void {
    this._isInternalUpdate = true;
    this.activeTabIndex = index;
    this._updateActiveTab();
    this._isInternalUpdate = false;
  }

  override updated(changedProperties: Map<string, unknown>): void {
    super.updated(changedProperties);
    if (changedProperties.has('activeTabIndex') && !this._isInternalUpdate) {
      this._updateActiveTab();
      this.requestUpdate();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-tabs': LudsTabs;
  }
}
